<script lang="ts" setup>
import Buttons from '~/components/redesign/Buttons';

import type { NuxtLinkProps } from '#app/components/nuxt-link';
import type { NuxtImgProps } from '#app/components/nuxt-img';

import { useI18n } from '#i18n';

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    tag?: 'div' | 'section';
    content: string;
    ctaLabel?: string;
    link: Partial<NuxtLinkProps>;
    logo: Pick<NuxtImgProps, 'sizes' | 'src' | 'densities' | 'alt'>;
  }>(),
  {
    tag: 'section',
  },
);

const ctaLabel = computed(() => props.ctaLabel ?? t('Read_Case_Study'));
</script>

<template>
  <component
    :is="tag"
    data-component-name="CTA.CasesPromo"
    class="bg-purple-gradient"
  >
    <div class="wrapper">
      <div class="left">
        <h2 v-html="content" />

        <Buttons.Link v-bind="link">
          {{ ctaLabel }}
          <SvgIcon name="arrow-forward" fill="#fff" />
        </Buttons.Link>
      </div>

      <div class="right">
        <NuxtImg
          src="/redesign/images/cta/cases-promo/ob-logo-white.png"
          alt="ObjectFirst logo"
          sizes="xs:192px md:266px xl:259px"
          densities="x1 x2"
          format="webp"
          loading="lazy"
          provider="cloudflare"
        />

        <NuxtImg
          v-bind="logo"
          format="webp"
          loading="lazy"
          provider="cloudflare"
        />
      </div>
    </div>

    <div class="image-wrapper" />
  </component>
</template>

<style lang="scss" scoped>
@use '_/mixins/flex';
@use '_/mixins/typo';
@use '_/mixins/size';
@use '_/mixins/media';
@use '_/fn';

[data-component-name='CTA.CasesPromo'] {
  position: relative;
  overflow: hidden;

  .wrapper {
    padding: 4rem 2rem;
    @include size.wrapper(80rem);

    @include flex.center-sb;
    gap: 2rem;

    @include media.tablet {
      flex-direction: column-reverse;
      gap: 4rem;
      padding: 2.5rem 2rem;
    }

    @include media.mobile {
      gap: 2rem;
      padding: 2.5rem 1rem;
    }

    .left,
    .right {
      z-index: 2;
    }

    .left {
      @include flex.start-sb;
      flex-direction: column;
      align-self: center;
      width: 43.5rem;

      @include media.tablet {
        width: 100%;
        max-width: 100%;
      }

      h2 {
        @include typo.subtitle-1;
        color: fn.token('text-reversed');

        margin-bottom: 2rem;
      }

      [data-component-name='Buttons.Link'] {
        @include media.mobile {
          width: 100%;
        }
      }
    }

    .right {
      @include flex.center-start;
      gap: 4rem;

      @include media.tablet {
        align-self: start;
      }

      @include media.mobile {
        gap: 2rem;
      }
    }
  }

  .image-wrapper {
    @include size.fixed(103rem);

    position: absolute;
    top: 0;
    right: 0;

    transform: translate(-22.7%, -27.1%) rotate(129.69deg);

    background-image: url('/redesign/images/figures/sphere-purple-1.webp');
    background-size: 100% 100%;

    @include media.tablet {
      transform: translate(17.1%, -22.5%) rotate(129.69deg);
    }

    @include media.mobile {
      transform: translate(31.1%, -37.1%) rotate(334.69deg);
    }
  }
}
</style>
